import React, { useState, useEffect, useCallback, useRef } from 'react';
import config from '../utils/config';
import axiosInstance from '../utils/axiosInstance';
import { isLocal } from '../utils/isLocal';
import Button from '../components/ui/Button';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/pilot-ui/select';

// Cross-device read-bundle feed. Lists every bundle landing in the
// oakleaf-read-bundles S3 bucket across ALL devices within a selectable window,
// newest first, auto-refreshing. Primary use: device bring-up — an operator does
// the 5-button-press on a fresh unit and watches here to confirm the bundle
// arrives, even before the device has a devices-table row.

interface BundleDevice {
  id: number;
  name: string;
  description?: string;
  city?: string;
  state?: string;
  entity_path?: string;
}

interface RecentBundle {
  key: string;
  device_name: string;
  last_modified: string;
  size: number;
  etag?: string;
  record_num?: number;
  day_num?: number;
  content_hash?: string;
  processed?: boolean;
  processed_at?: string;
  known: boolean;
  device?: BundleDevice;
}

interface RecentBundlesResponse {
  window: string;
  since_utc: string;
  generated_at: string;
  count: number;
  truncated: boolean;
  bundles: RecentBundle[];
  errors: string[];
}

interface BundleDetail {
  device_name: string;
  key: string;
  last_modified: string;
  size: number;
  content_kind: string;
  content: string;
}

const WINDOW_OPTIONS: { label: string; value: string }[] = [
  { label: 'Last 15 min', value: '15m' },
  { label: 'Last hour', value: '1h' },
  { label: 'Last 3 hours', value: '3h' },
  { label: 'Last 6 hours', value: '6h' },
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 7 days', value: '7d' },
];

const REFRESH_OPTIONS: { label: string; value: number }[] = [
  { label: 'None', value: 0 },
  { label: '10 seconds', value: 10000 },
  { label: '30 seconds', value: 30000 },
  { label: '60 seconds', value: 60000 },
  { label: '5 minutes', value: 300000 },
];

const LIMIT = 200;
const HIGHLIGHT_MS = 6000;
const FILTER_DEBOUNCE_MS = 400;

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const relativeTime = (iso: string, now: number): string => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const secs = Math.max(0, Math.round((now - t) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const RecentBundlesPage: React.FC = () => {
  const [windowSel, setWindowSel] = useState('1h');
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [refreshMs, setRefreshMs] = useState(10000);
  const [data, setData] = useState<RecentBundlesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(Date.now());

  const [highlightKeys, setHighlightKeys] = useState<Set<string>>(new Set());
  const seenKeysRef = useRef<Set<string>>(new Set());
  const firstLoadRef = useRef(true);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Expanded-row state (lazy bundle content fetch).
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [detail, setDetail] = useState<BundleDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const fetchFeed = useCallback(async (win: string, device: string) => {
    if (!config.recentBundlesApiUrl) {
      setError('Recent-bundles API URL is not configured.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      let url = `${config.recentBundlesApiUrl}?window=${encodeURIComponent(win)}&limit=${LIMIT}`;
      if (device.trim()) {
        url += `&device=${encodeURIComponent(device.trim())}`;
      }
      const resp = await axiosInstance.get<RecentBundlesResponse>(url);
      const bundles = resp.data.bundles || [];

      // Flag bundles whose key we have not seen before (skip the very first
      // load so the initial page doesn't light up entirely).
      if (!firstLoadRef.current) {
        const fresh = new Set<string>();
        bundles.forEach((b) => {
          if (!seenKeysRef.current.has(b.key)) fresh.add(b.key);
        });
        if (fresh.size > 0) {
          setHighlightKeys(fresh);
          if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
          highlightTimerRef.current = setTimeout(() => setHighlightKeys(new Set()), HIGHLIGHT_MS);
        }
      }
      bundles.forEach((b) => seenKeysRef.current.add(b.key));
      firstLoadRef.current = false;

      setData(resp.data);
      setLastUpdated(Date.now());
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError?.response?.data?.error || 'Failed to fetch recent bundles.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce the filter input before it drives a (server-side) re-fetch.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(filter), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filter]);

  // Re-fetch whenever the window or device filter changes; reset the seen-set
  // so a wider window / new filter doesn't flash every newly-revealed bundle.
  useEffect(() => {
    seenKeysRef.current = new Set();
    firstLoadRef.current = true;
    setHighlightKeys(new Set());
    fetchFeed(windowSel, debouncedFilter);
  }, [windowSel, debouncedFilter, fetchFeed]);

  // Auto-refresh poll (refreshMs === 0 means off).
  useEffect(() => {
    if (refreshMs > 0) {
      intervalRef.current = setInterval(() => fetchFeed(windowSel, debouncedFilter), refreshMs);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [refreshMs, windowSel, debouncedFilter, fetchFeed]);

  // Tick for relative-time labels.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => () => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
  }, []);

  const toggleExpand = useCallback(async (b: RecentBundle) => {
    if (expandedKey === b.key) {
      setExpandedKey(null);
      setDetail(null);
      setDetailError('');
      return;
    }
    setExpandedKey(b.key);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const url = `${config.recentBundlesApiUrl}?action=get&key=${encodeURIComponent(b.key)}`;
      const resp = await axiosInstance.get<BundleDetail>(url);
      setDetail(resp.data);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setDetailError(axiosError?.response?.data?.error || 'Failed to fetch bundle content.');
    } finally {
      setDetailLoading(false);
    }
  }, [expandedKey]);

  const bundles = data?.bundles || [];
  // True while the typed filter has not yet propagated to a fetched result.
  const filterPending = filter.trim() !== debouncedFilter.trim();

  return (
    <div className="h-full w-full bg-gray-50" style={isLocal() ? { border: '4px solid red' } : {}}>
      {isLocal() && (
        <div className="bg-red-600 text-white text-center p-2">
          <strong>Local Environment</strong>
        </div>
      )}
      <div className="max-w-7xl mx-auto p-2">
        <div className="bg-card border border-border rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h1 className="text-2xl font-bold">Read Bundle Feed</h1>
            <div className="text-sm text-muted-foreground">
              {lastUpdated && <span>Updated {relativeTime(new Date(lastUpdated).toISOString(), nowTick)}</span>}
              {loading && <span className="ml-2 text-status-info-foreground">refreshing…</span>}
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            Bundles arriving across all devices, newest first. Brand-new devices with no record
            appear with an <span className="font-semibold text-amber-800">UNKNOWN</span> badge.
          </p>

          {/* Controls */}
          <div className="flex items-end gap-4 mb-4 flex-wrap">
            <div>
              <Label htmlFor="bundle-window">Time window</Label>
              <Select value={windowSel} onValueChange={value => { if (value) setWindowSel(value); }} items={WINDOW_OPTIONS}>
                <SelectTrigger id="bundle-window" title="How far back to load read bundles" aria-describedby="bundle-window-help"><SelectValue /></SelectTrigger>
                <SelectContent>{WINDOW_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <p id="bundle-window-help" className="text-xs text-muted-foreground">Newest bundles within the selected window.</p>
            </div>
            <div className="grow min-w-[200px]">
              <Label htmlFor="bundle-device-filter" className="block text-xs font-medium text-gray-600 mb-1">
                Filter by device ID (substring)
              </Label>
              <Input
                id="bundle-device-filter"
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="e.g. 430022 or full thing ID"
                className="w-full font-mono"
                title="Search bundle device IDs by all or part of the ID"
                aria-describedby="bundle-device-filter-help"
              />
              <p id="bundle-device-filter-help" className="text-xs text-muted-foreground">Accepts part of an ID; results update after typing stops.</p>
            </div>
            <div>
              <Label htmlFor="bundle-refresh">Auto-refresh</Label>
              <Select value={String(refreshMs)} onValueChange={value => setRefreshMs(Number(value))} items={REFRESH_OPTIONS.map(o => ({ value: String(o.value), label: o.label }))}>
                <SelectTrigger id="bundle-refresh" title="How often to fetch newly arrived bundles" aria-describedby="bundle-refresh-help"><SelectValue /></SelectTrigger>
                <SelectContent>{REFRESH_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <p id="bundle-refresh-help" className="text-xs text-muted-foreground">None pauses automatic updates.</p>
            </div>
            <Button
              variant="primary"
              onClick={() => fetchFeed(windowSel, debouncedFilter)}
              title="Load the newest bundles for this time window and device filter now, without waiting for the next automatic refresh"
            >
              Refresh now
            </Button>
          </div>

          {error && (
            <div role="alert" className="bg-red-50 border border-status-error-border text-red-700 text-sm rounded-sm p-3 mb-4">
              {error}
            </div>
          )}

          {data?.truncated && (
            <div className="bg-amber-50 border border-status-warning-border text-amber-800 text-sm rounded-sm p-2 mb-3">
              Showing the {data.count} most recent bundles — older ones in this window are not shown.
              Narrow the window to see fewer, or this is the cap (200).
            </div>
          )}

          {data?.errors && data.errors.length > 0 && (
            <div className="bg-amber-50 border border-status-warning-border text-amber-800 text-xs rounded-sm p-2 mb-3">
              {data.errors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}

          <div className="text-xs text-muted-foreground mb-2">
            {bundles.length} bundle{bundles.length === 1 ? '' : 's'}
            {debouncedFilter.trim() ? ` matching "${debouncedFilter.trim()}"` : ''}
            {filterPending ? ' …' : ''}
          </div>

          {/* Table */}
          <div className="overflow-x-auto border rounded-sm">
            <table className="min-w-full text-sm">
              <thead className="border border-status-neutral-border bg-status-neutral text-status-neutral-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Arrived</th>
                  <th className="text-left px-3 py-2 font-medium">Device</th>
                  <th className="text-left px-3 py-2 font-medium">Identity</th>
                  <th className="text-left px-3 py-2 font-medium">Processed</th>
                  <th className="text-right px-3 py-2 font-medium">Rec #</th>
                  <th className="text-right px-3 py-2 font-medium">Day #</th>
                  <th className="text-right px-3 py-2 font-medium">Size</th>
                </tr>
              </thead>
              <tbody>
                {bundles.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted-foreground py-8">
                      No bundles in this window{debouncedFilter.trim() ? ` for "${debouncedFilter.trim()}"` : ''}.
                    </td>
                  </tr>
                )}
                {bundles.map((b) => {
                  const isNew = highlightKeys.has(b.key);
                  const isExpanded = expandedKey === b.key;
                  return (
                    <React.Fragment key={b.key}>
                      <tr
                        onClick={() => toggleExpand(b)}
                        className={`border-t cursor-pointer transition-colors duration-1000 ${
                          isNew ? 'bg-green-100' : isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-3 py-2 whitespace-nowrap" title={b.last_modified}>
                          <Button variant="link" title={`${isExpanded ? 'Hide' : 'Show'} bundle ${b.key}`} aria-label={`${isExpanded ? 'Hide' : 'Show'} bundle ${b.key}`} aria-expanded={isExpanded} aria-controls={isExpanded ? `bundle-detail-${encodeURIComponent(b.key)}` : undefined} onClick={event => {
                            event.stopPropagation();
                            toggleExpand(b);
                          }}>
                            {relativeTime(b.last_modified, nowTick)}
                          </Button>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                          {b.device_name}
                        </td>
                        <td className="px-3 py-2">
                          {b.known && b.device ? (
                            <div>
                              <span className="inline-block border border-status-neutral-border bg-status-neutral text-status-neutral-foreground text-xs px-1.5 py-0.5 rounded-sm mr-1">
                                #{b.device.id}
                              </span>
                              <span className="text-gray-800">
                                {b.device.description || b.device.name}
                              </span>
                              {(b.device.city || b.device.state) && (
                                <span className="text-muted-foreground text-xs ml-1">
                                  {[b.device.city, b.device.state].filter(Boolean).join(', ')}
                                </span>
                              )}
                              {b.device.entity_path && (
                                <span className="block text-muted-foreground text-xs font-mono">
                                  {b.device.entity_path}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="inline-block border border-status-warning-border bg-status-warning text-status-warning-foreground text-xs font-semibold px-2 py-0.5 rounded-sm">
                              UNKNOWN — no device record
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {b.processed === undefined ? (
                            <span className="text-muted-foreground text-xs">—</span>
                          ) : b.processed ? (
                            <span
                              className="inline-block border border-status-success-border bg-status-success text-status-success-foreground text-xs px-2 py-0.5 rounded-sm"
                              title={b.processed_at ? `at ${b.processed_at}` : undefined}
                            >
                              ingested
                            </span>
                          ) : (
                            <span className="inline-block border border-status-error-border bg-status-error text-status-error-foreground text-xs px-2 py-0.5 rounded-sm">
                              pending
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {b.record_num ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {b.day_num ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                          {formatBytes(b.size)}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr id={`bundle-detail-${encodeURIComponent(b.key)}`} className="border-t bg-gray-50">
                          <td colSpan={7} className="px-3 py-3">
                            <div className="text-xs text-muted-foreground mb-2 font-mono break-all">
                              {b.key}
                            </div>
                            {detailLoading && <div className="text-sm text-muted-foreground">Loading bundle…</div>}
                            {detailError && <div role="alert" className="text-sm text-status-error-foreground">{detailError}</div>}
                            {detail && (
                              <pre className="bg-white border rounded-sm p-3 text-xs overflow-x-auto max-h-96">
                                {detail.content}
                              </pre>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentBundlesPage;
